import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * BRAND MARKS
 *
 * The product's own mark and its partners' — Square, RoadSync, Apple Pay,
 * Google Pay, QuickBooks, Urgently. A logo is not a Thumbnail and not an
 * Avatar: nothing is drawn behind it, nothing is drawn around it, and it is
 * never cropped. Twelve sites in the app wrote the `<img>` by hand, each
 * choosing its own size and each importing the artwork itself.
 *
 * Two shapes. A *square* mark takes one size on both axes so the box is
 * stable before the file arrives; a *wide* mark — a wordmark, the Apple Pay
 * and Google Pay badges — takes the height and lets its own ratio set the
 * width.
 *
 * Written out per breakpoint, like Grid's columns: `sm:${cls}` built at
 * runtime produces no CSS.
 */
const SQUARE = {
  base: {
    xs: 'size-4',
    sm: 'size-6',
    md: 'size-8',
    lg: 'size-12',
    xl: 'size-16',
  },
  sm: {
    xs: 'sm:size-4',
    sm: 'sm:size-6',
    md: 'sm:size-8',
    lg: 'sm:size-12',
    xl: 'sm:size-16',
  },
  md: {
    xs: 'md:size-4',
    sm: 'md:size-6',
    md: 'md:size-8',
    lg: 'md:size-12',
    xl: 'md:size-16',
  },
} as const;

const WIDE = {
  base: { xs: 'h-4', sm: 'h-6', md: 'h-8', lg: 'h-12', xl: 'h-16' },
  sm: {
    xs: 'sm:h-4',
    sm: 'sm:h-6',
    md: 'sm:h-8',
    lg: 'sm:h-12',
    xl: 'sm:h-16',
  },
  md: {
    xs: 'md:h-4',
    sm: 'md:h-6',
    md: 'md:h-8',
    lg: 'md:h-12',
    xl: 'md:h-16',
  },
} as const;

export type BrandMarkSize = keyof (typeof SQUARE)['base'];
export type BrandMarkShape = 'square' | 'wide';
/** One size everywhere, or a size per breakpoint: `{ base: 'sm', sm: 'md' }`. */
export type BrandMarkSizes =
  | BrandMarkSize
  | Partial<Record<keyof typeof SQUARE, BrandMarkSize>>;

/** The size classes for a mark of this shape, at every breakpoint named. */
export function brandMarkSize(
  size: BrandMarkSizes | undefined,
  shape: BrandMarkShape
): string {
  const table = shape === 'wide' ? WIDE : SQUARE;
  if (size === undefined) return table.base.md;
  if (typeof size === 'string') return table.base[size];
  return (Object.keys(table) as (keyof typeof table)[])
    .filter((bp) => size[bp] !== undefined)
    .map((bp) => table[bp][size[bp]!])
    .join(' ');
}

/** Everything about a mark except its size. */
export const brandMarkRecipe = cva(
  [uiMarker, 'inline-block shrink-0 select-none object-contain'],
  {
    variants: {
      shape: {
        square: '',
        /** The width follows the artwork's own ratio. */
        wide: 'w-auto',
      },
    },
    defaultVariants: { shape: 'square' },
  }
);

export type BrandMarkRecipeProps = VariantProps<typeof brandMarkRecipe>;
